/**
 * CampaignRankingUserItem
 *
 * One row in the user (most-subscribers) ranking leaderboard.
 *
 * Mirrors Flutter's `CampainRankingUserItem`
 * (lib/presentation/screens/campaigns/widgets/campain_ranking_user_item.dart):
 * - `:30-35` — leading circular avatar from `generateUserProfileImageUrl(firebaseId)`.
 * - `:45-56` — winner icon (`assets/images/campaign.png`, tinted `exclusive`),
 *   shown only when `winner`.
 * - `:57-67` — name shows "You" for the current user, else `userName`.
 * - `:75-82` — subtitle `"{count} subscribers"`.
 * - `:20-24` — whole row tap → `onTap(firebaseId)`.
 *
 * Web tokens (theme / responsive / l10n) follow web conventions; the avatar
 * reuses the established `CachedImage` + `UserUtils.generateUserProfileImageUrl`
 * pattern (matches `SearchUserItem`), and the winner icon reuses `AssetIcon`
 * mask-tinting (matches `NotificationCampaignRankingItem`).
 */
import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { UserUtils } from '@/domain/utils/userUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { CampaignRankingUserEntity } from '@/domain/entities/campaign/campaignRankingUserEntity';

export interface CampaignRankingUserItemProps {
  user: CampaignRankingUserEntity;
  isCurrentUser: boolean;
  onTap: (firebaseId: string) => void;
}

export const CampaignRankingUserItem: React.FC<CampaignRankingUserItemProps> = ({
  user,
  isCurrentUser,
  onTap,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const displayName = isCurrentUser ? l10n.campaigns.you : user.userName;
  const subscribersText = l10n.campaigns.subscribersCount.replace(
    '{count}',
    String(user.subscribers),
  );

  return (
    <div
      {...qaAttr({ id: `campaign-rank-user-${user.firebaseId}` })}
      onClick={() => onTap(user.firebaseId)}
      style={{
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
      }}
    >
      <CachedImage
        src={UserUtils.generateUserProfileImageUrl(user.firebaseId)}
        alt={displayName}
        style={{
          width: responsive.avatar.large,
          height: responsive.avatar.large,
          borderRadius: responsive.borderRadius.round,
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />

      <div style={{ flex: 1, marginLeft: responsive.spacing.base, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: responsive.spacing.xs }}>
          {user.winner && (
            <AssetIcon
              path="/assets/images/campaign.png"
              color={theme.exclusive}
              width={responsive.icon.small}
              height={responsive.icon.small}
            />
          )}
          <span
            style={{
              fontWeight: responsive.fontWeight.semibold,
              color: theme.text.primary,
              fontSize: responsive.font.base,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </span>
        </div>
        <span
          style={{
            display: 'block',
            marginTop: responsive.spacing.xs,
            fontSize: responsive.font.sm,
            color: theme.text.secondary,
          }}
        >
          {subscribersText}
        </span>
      </div>
    </div>
  );
};
